import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import styled from "styled-components";
import PrayerRequestDialog from "./dialogs/PrayerRequestDialog";
import PrayerTypeDialog from "./dialogs/PrayerTypeDialog";
import { MoreVert, ArrowRight } from "@mui/icons-material";
import {
  PrayerRequestContext,
  getStatusFilterForTab,
} from "./../contexts/PrayerRequestContext";
import { useConfiguration } from "../contexts/ConfigurationContext";
import {
  Menu,
  MenuItem,
  IconButton as MuiIconButton,
  Checkbox,
  Divider,
} from "@mui/material";
import { callTypes } from "../assets/data";
import { Trash, HandsPraying, ShareFat, ChartBar } from "@phosphor-icons/react";
import Reports from "./Reports";

// Import Space Grotesk font
import "@fontsource/space-grotesk/300.css";
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";

// Performance monitoring hook
const usePerformanceMonitor = (componentName) => {
  useEffect(() => {
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      if (endTime - startTime > 16) {
        // Log if render takes longer than 16ms (60fps)
        console.warn(
          `${componentName} render took ${(endTime - startTime).toFixed(2)}ms`
        );
      }
    };
  });
};

// Update the sources constant to match validation rules
const SOURCES = [
  "online_form",
  "email",
  "snail_mail",
  "teleministry",
  "other",
  "WePray",
  "prayer_center",
];

// Departments are now loaded dynamically from ConfigurationContext

const PrayerRequestDashboard = () => {
  // Performance monitoring
  usePerformanceMonitor("PrayerRequestDashboard");

  const {
    createPrayerRequest,
    updatePrayerRequest,
    fetchPrayerRequests,
    fetchCounts,
    prayerRequests,
    counts: contextCounts,
    totalCount: contextTotalCount,
    forwardPrayerRequest,
    recoverPrayerRequest,
  } = useContext(PrayerRequestContext);

  // Get departments from configuration context
  const { departments } = useConfiguration();

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);

  // Update initial state to match creation validation rules
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("submitted");
  const [newRequest, setNewRequest] = useState({
    requestedBy: "",
    email: "",
    source: "",
    category: "",
    prayer_request: "",
    notes: "",
    targetPrayerCount: 1,
    currentPrayerCount: 0,
  });
  const [editingRequest, setEditingRequest] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [categoryMenuAnchorEl, setCategoryMenuAnchorEl] = useState(null);
  const [shareMenuAnchorEl, setShareMenuAnchorEl] = useState(null);
  const [selectedDepartments, setSelectedDepartments] = useState({});
  const [showCurrentMonth, setShowCurrentMonth] = useState(false);
  // Report section date filter (lifted so we can pass date_range to API)
  const [reportShowCurrentMonth, setReportShowCurrentMonth] = useState(false);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [prayerTypeDialogOpen, setPrayerTypeDialogOpen] = useState(false);
  const [prayerRequestForType, setPrayerRequestForType] = useState(null);

  // Fetch counts once on mount (for tab badges); real-time list is fetched per tab below
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Build date_range string for Reports API (format: "YYYY-MM-DD,YYYY-MM-DD")
  const getReportDateRange = useCallback(() => {
    if (reportShowCurrentMonth) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return `${start.toISOString().slice(0, 10)},${end.toISOString().slice(0, 10)}`;
    }
    if (reportStartDate && reportEndDate) {
      return `${reportStartDate},${reportEndDate}`;
    }
    if (reportStartDate) {
      return `${reportStartDate},${reportStartDate}`;
    }
    if (reportEndDate) {
      return `${reportEndDate},${reportEndDate}`;
    }
    return null;
  }, [reportShowCurrentMonth, reportStartDate, reportEndDate]);

  // Fetch filtered data from API when tab or page changes (real-time filtered from API)
  useEffect(() => {
    if (selectedTab === "reports") {
      const date_range = getReportDateRange();
      fetchPrayerRequests({
        perPage: 0,
        page: 1,
        ...(date_range && { date_range }),
      });
      return;
    }
    const status = getStatusFilterForTab(selectedTab);
    fetchPrayerRequests({
      status,
      page: currentPage,
      perPage: itemsPerPage,
    });
  }, [
    selectedTab,
    currentPage,
    itemsPerPage,
    fetchPrayerRequests,
    getReportDateRange,
  ]);

  // Set up periodic refresh every 60 seconds so data stays in sync with API
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchCounts();
      if (selectedTab === "reports") {
        const date_range = getReportDateRange();
        fetchPrayerRequests({
          perPage: 0,
          page: 1,
          ...(date_range && { date_range }),
        });
      } else {
        const status = getStatusFilterForTab(selectedTab);
        fetchPrayerRequests({
          status,
          page: currentPage,
          perPage: itemsPerPage,
        });
      }
    }, 60000);
    return () => clearInterval(intervalId);
  }, [
    selectedTab,
    currentPage,
    itemsPerPage,
    fetchPrayerRequests,
    fetchCounts,
    getReportDateRange,
  ]);

  // Add handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Format the request with max length constraints
      const formattedRequest = {
        ...newRequest,
        requestedBy: newRequest.requestedBy?.slice(0, 255) || "",
        prayer_request: newRequest.prayer_request?.slice(0, 500) || "",
        notes: newRequest.notes?.slice(0, 1000) || "",
        email: newRequest.email?.slice(0, 255) || null,
        source: newRequest.source || "",
        category: newRequest.category || "",
        targetPrayerCount: Math.max(
          1,
          parseInt(newRequest.targetPrayerCount) || 1
        ),
        currentPrayerCount: 0,
      };

      await createPrayerRequest(formattedRequest);
      setOpenDialog(false);
      setNewRequest({
        requestedBy: "",
        email: "",
        source: "",
        category: "",
        prayer_request: "",
        notes: "",
        targetPrayerCount: 1,
        currentPrayerCount: 0,
      });

      // No need to refresh - createPrayerRequest already updates the state
    } catch (error) {
      console.error("Error creating prayer request:", error);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Format the request with max length constraints
      const formattedRequest = {
        ...editingRequest,
        requestedBy: editingRequest.requestedBy?.slice(0, 255) || "",
        prayer_request: editingRequest.prayer_request?.slice(0, 500) || "",
        notes: editingRequest.notes?.slice(0, 1000) || "",
        email: editingRequest.email?.slice(0, 255) || null,
        source: editingRequest.source || "",
        category: editingRequest.category || "",
        targetPrayerCount: Math.max(
          1,
          parseInt(editingRequest.targetPrayerCount) || 1
        ),
      };

      // Get the original request to compare changes
      const originalRequest = prayerRequests.find(
        (req) => req.id === editingRequest.id
      );

      // Check if any fields have changed
      const hasChanges = Object.keys(formattedRequest).some(
        (key) => formattedRequest[key] !== originalRequest[key]
      );

      if (hasChanges) {
        await updatePrayerRequest(editingRequest.id, formattedRequest);
        setEditDialogOpen(false);
      }
    } catch (error) {
      console.error("Error updating prayer request:", error);
    }
  };

  const handleEdit = useCallback((request) => {
    // Format request for editing with nullable fields
    const formattedRequest = {
      ...request,
      title: request.title || "",
      prayer_request: request.prayer_request || "",
      requestedBy: request.requestedBy || "",
      email: request.email || "",
      source: request.source || "online_form",
      category: request.category || "",
      status: request.status || "new",
      notes: request.notes || "",
      processedBy: request.processedBy || "",
      prayedBy: request.prayedBy || "",
      userId: request.userId || "",
      organizationId: request.organizationId || "",
      targetPrayerCount: request.targetPrayerCount || 1,
      currentPrayerCount: request.currentPrayerCount || 0,
    };
    setEditingRequest(formattedRequest);
    setEditDialogOpen(true);
  }, []);

  const handleMenuClick = useCallback((event, request) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedRequest(null);
  }, []);

  const handleEditFromMenu = useCallback(() => {
    if (selectedRequest) {
      handleEdit(selectedRequest);
      handleMenuClose();
    }
  }, [selectedRequest, handleMenuClose, handleEdit]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        await updatePrayerRequest(id, {
          status: "deleted",
        });
        // No need to call fetchPrayerRequests() as the context already updates the state
      } catch (error) {
        console.error("Error deleting prayer request:", error);
      }
    },
    [updatePrayerRequest]
  );

  const handlePrayedFor = useCallback(
    async (id) => {
      try {
        const request = prayerRequests.find((req) => req.id === id);
        const isCurrentlyPrayed = request.prayedBy && request.prayedDate;

        // If currently prayed, unmark it
        if (isCurrentlyPrayed) {
          await updatePrayerRequest(id, {
            prayedBy: null,
            prayedDate: null,
            status: "processed",
          });
        } else {
          // If in new state, show category selection dialog first
          if (request.status === "new") {
            setPrayerRequestForType(request);
            setPrayerTypeDialogOpen(true);
            return;
          }

          // For other states, proceed with normal prayer logic
          const newCount = (request.currentPrayerCount || 0) + 1;
          const hasReachedTarget = newCount >= (request.targetPrayerCount || 1);

          await updatePrayerRequest(id, {
            prayedBy: "Current User", // Replace with actual user
            prayedDate: new Date().toISOString(),
            status: hasReachedTarget ? "prayed" : "pending",
            currentPrayerCount: newCount,
          });
        }

        // No need to call fetchPrayerRequests() as the context already updates the state
      } catch (error) {
        console.error("Error updating prayed for status:", error);
      }
    },
    [updatePrayerRequest, prayerRequests]
  );

  const handlePrayerTypeConfirm = useCallback(
    async (category) => {
      try {
        if (prayerRequestForType) {
          const newCount = (prayerRequestForType.currentPrayerCount || 0) + 1;
          const hasReachedTarget =
            newCount >= (prayerRequestForType.targetPrayerCount || 1);

          await updatePrayerRequest(prayerRequestForType.id, {
            prayedBy: "Current User", // Replace with actual user
            prayedDate: new Date().toISOString(),
            status: hasReachedTarget ? "prayed" : "pending",
            currentPrayerCount: newCount,
            category: category, // Set the category to the selected category
          });
        }
      } catch (error) {
        console.error("Error updating prayer with category:", error);
      }
    },
    [updatePrayerRequest, prayerRequestForType]
  );

  const handleCategoryUpdate = useCallback(
    async (category) => {
      if (selectedRequest) {
        try {
          await updatePrayerRequest(selectedRequest.id, {
            category: category,
            status: "pending", // Set status to pending when category is set
          });
          handleCategoryMenuClose();
          handleMenuClose();
          // No need to call fetchPrayerRequests() as the context already updates the state
        } catch (error) {
          console.error("Error updating category:", error);
        }
      }
    },
    [updatePrayerRequest, selectedRequest]
  );

  const handleCategoryMenuClick = useCallback((event) => {
    event.stopPropagation();
    setCategoryMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCategoryMenuClose = useCallback(() => {
    setCategoryMenuAnchorEl(null);
  }, []);

  const handleCategorySelect = useCallback(
    (category) => {
      if (selectedRequest) {
        handleCategoryUpdate(category);
        handleCategoryMenuClose();
        handleMenuClose();
      }
    },
    [
      selectedRequest,
      handleCategoryUpdate,
      handleCategoryMenuClose,
      handleMenuClose,
    ]
  );

  const handleShareClick = useCallback((event, request) => {
    event.stopPropagation();
    setShareMenuAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  }, []);

  const handleShareMenuClose = useCallback(() => {
    setShareMenuAnchorEl(null);
    setSelectedRequest(null);
  }, []);

  const handleDepartmentToggle = useCallback(
    (departmentId) => {
      const newSelected = { ...selectedDepartments };
      newSelected[departmentId] = !newSelected[departmentId];
      setSelectedDepartments(newSelected);
    },
    [selectedDepartments]
  );

  const handleForwardSubmit = useCallback(async () => {
    if (selectedRequest) {
      try {
        // Get all selected departments
        const selectedDepts = Object.entries(selectedDepartments)
          .filter(([_, isSelected]) => isSelected)
          .map(([deptId]) => departments.find((d) => d.id === parseInt(deptId)))
          .filter(Boolean);

        // Forward to each selected department
        for (const dept of selectedDepts) {
          await forwardPrayerRequest(selectedRequest.id, {
            recipientEmail: dept.email,
            recipientName: dept.name,
          });
        }

        // Reset selections and close menu
        setSelectedDepartments({});
        handleShareMenuClose();

        // No need to call fetchPrayerRequests() as the context already updates the state
      } catch (error) {
        console.error("Error forwarding prayer request:", error);
      }
    }
  }, [
    selectedRequest,
    selectedDepartments,
    forwardPrayerRequest,
    handleShareMenuClose,
  ]);

  const handleRecover = useCallback(
    async (id) => {
      try {
        await recoverPrayerRequest(id);
        // No need to call fetchPrayerRequests() as the context already updates the state
      } catch (error) {
        console.error("Error recovering prayer request:", error);
      }
    },
    [recoverPrayerRequest]
  );

  // Data is filtered in real time from the API via PrayerRequestContext
  // prayerRequests from context = current tab's page of data (or full list for reports)
  const paginatedRequests = prayerRequests;

  // Use counts from context (from API meta or fetchCounts); fallback to 0 so tabs render
  const counts = useMemo(
    () =>
      contextCounts ?? {
        submitted: 0,
        pending: 0,
        processed: 0,
        deleted: 0,
      },
    [contextCounts]
  );

  // Total items: from API when server-side pagination (totalCount), else length of current list
  const totalItems =
    contextTotalCount != null ? contextTotalCount : prayerRequests.length;

  // Memoized total pages
  const totalPages = useMemo(
    () => Math.ceil(totalItems / itemsPerPage),
    [totalItems, itemsPerPage]
  );

  // Memoized active requests
  const activeRequests = useMemo(() => {
    const isNotDeleted = (req) => req.status !== "deleted";
    if (showCurrentMonth) {
      const now = new Date();
      return prayerRequests.filter((req) => {
        const requestDate = new Date(req.created_at);
        const isCurrentMonth =
          requestDate.getMonth() === now.getMonth() &&
          requestDate.getFullYear() === now.getFullYear();
        return isNotDeleted(req) && isCurrentMonth;
      });
    }
    return prayerRequests.filter(isNotDeleted);
  }, [prayerRequests, showCurrentMonth]);

  // Add pagination handlers
  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  const handleTabChange = useCallback((tab) => {
    setSelectedTab(tab);
    setCurrentPage(1);
  }, []);

  // Define the getCounts function
  // const getCounts = () => ({ ... }); - REMOVED

  // Use state to store counts and update them when prayerRequests changes
  // const [counts, setCounts] = useState(getCounts()); - REMOVED

  // Update counts whenever prayerRequests changes
  // useEffect(() => { setCounts(getCounts()); }, [prayerRequests]); - REMOVED

  return (
    <Wrapper>
      <Header>
        <HeaderTop>
          <h1>Prayer Basket</h1>
          <NewRequestButton onClick={() => setOpenDialog(true)}>
            NEW PRAYER REQUEST
          </NewRequestButton>
        </HeaderTop>
        <TabContainer>
          <Tab
            active={selectedTab === "submitted"}
            onClick={() => handleTabChange("submitted")}
            tab="submitted"
          >
            PRAYERS SUBMITTED
            <TabCount $active={selectedTab === "submitted"} tab="submitted">
              {counts.submitted}
            </TabCount>
          </Tab>
          <Tab
            active={selectedTab === "pending"}
            onClick={() => handleTabChange("pending")}
            tab="pending"
          >
            PENDING PRAYERS
            <TabCount $active={selectedTab === "pending"} tab="pending">
              {counts.pending}
            </TabCount>
          </Tab>
          <Tab
            active={selectedTab === "processed"}
            onClick={() => handleTabChange("processed")}
            tab="processed"
          >
            PRAYERS PROCESSED
            <TabCount $active={selectedTab === "processed"} tab="processed">
              {counts.processed}
            </TabCount>
          </Tab>
          <Tab
            active={selectedTab === "deleted"}
            onClick={() => handleTabChange("deleted")}
            tab="deleted"
          >
            PRAYERS DELETED
            <TabCount $active={selectedTab === "deleted"} tab="deleted">
              {counts.deleted}
            </TabCount>
          </Tab>
          <Tab
            active={selectedTab === "reports"}
            onClick={() => handleTabChange("reports")}
            tab="reports"
          >
            <ChartBar
              size={24}
              weight={selectedTab === "reports" ? "fill" : "regular"}
            />
          </Tab>
        </TabContainer>
      </Header>

      {selectedTab === "reports" ? (
        <Reports
          prayerRequests={prayerRequests}
          showCurrentMonth={reportShowCurrentMonth}
          onShowCurrentMonthChange={setReportShowCurrentMonth}
          startDate={reportStartDate}
          endDate={reportEndDate}
          onStartDateChange={setReportStartDate}
          onEndDateChange={setReportEndDate}
          onClearDateFilters={() => {
            setReportStartDate("");
            setReportEndDate("");
          }}
        />
      ) : (
        <CardGrid>
          {paginatedRequests.map((request) => (
            <Card key={request.id}>
              <CardHeader>
                <UserInfo>
                  <UserName>{request.requestedBy || "Anonymous"}</UserName>
                  <UserEmail>{request.email || "No email provided"}</UserEmail>
                </UserInfo>
                <MuiIconButton
                  onClick={(e) => handleMenuClick(e, request)}
                  size="small"
                >
                  <MoreVert />
                </MuiIconButton>
              </CardHeader>
              <CardContent>
                {request.prayer_request || "No prayer request provided"}
              </CardContent>
              <CardActions>
                {request.status === "deleted" ? (
                  <>
                    <RecoverButton onClick={() => handleRecover(request.id)}>
                      Recover request
                    </RecoverButton>
                    <SourceLabel>
                      {request.source
                        ? request.source.replace("_", " ").toUpperCase()
                        : "SOURCE NOT SPECIFIED"}
                    </SourceLabel>
                  </>
                ) : (
                  <>
                    <DeleteButton onClick={() => handleDelete(request.id)}>
                      <Trash size={24} color="#EC3310" />
                    </DeleteButton>
                    <SourceLabel>
                      {request.source
                        ? request.source.replace("_", " ").toUpperCase()
                        : "SOURCE NOT SPECIFIED"}
                    </SourceLabel>
                    <ActionButtons>
                      {request.status === "prayed" ? (
                        <PrayedButton disabled $isPrayed={true}>
                          <HandsPraying
                            size={24}
                            weight="fill"
                            color="#2DB3C0"
                          />
                        </PrayedButton>
                      ) : request.status === "processed" ? (
                        <ShareButton disabled>
                          <ShareFat size={24} weight="fill" color="#FF7234" />
                        </ShareButton>
                      ) : (
                        <>
                          <ShareButton
                            onClick={(e) => handleShareClick(e, request)}
                          >
                            <ShareFat
                              size={24}
                              weight="regular"
                              color="#FF7234"
                            />
                          </ShareButton>
                          <PrayedButton
                            onClick={() => handlePrayedFor(request.id)}
                            $isPrayed={false}
                          >
                            <HandsPraying
                              size={24}
                              weight="regular"
                              color="#2DB3C0"
                            />
                          </PrayedButton>
                        </>
                      )}
                    </ActionButtons>
                  </>
                )}
              </CardActions>
              <PrayerCountInfo>
                <PrayerCount>
                  Prayers: {request.currentPrayerCount || 0} /{" "}
                  {request.targetPrayerCount || 1}
                </PrayerCount>
                {request.currentPrayerCount >=
                  (request.targetPrayerCount || 1) && (
                  <PrayerCompleteBadge>Prayer Goal Reached</PrayerCompleteBadge>
                )}
              </PrayerCountInfo>
            </Card>
          ))}
        </CardGrid>
      )}

      {selectedTab !== "reports" && totalItems > itemsPerPage && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            items
          </PaginationInfo>
          <PaginationControls>
            <PaginationButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            <PageNumbers>
              {(() => {
                const pages = [];
                const maxVisiblePages = 5;

                if (totalPages <= maxVisiblePages) {
                  // Show all pages if total pages is less than max visible
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(
                      <PageButton
                        key={i}
                        active={currentPage === i}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </PageButton>
                    );
                  }
                } else {
                  // Always show first page
                  pages.push(
                    <PageButton
                      key={1}
                      active={currentPage === 1}
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </PageButton>
                  );

                  // Calculate start and end of visible pages
                  let startPage = Math.max(2, currentPage - 1);
                  let endPage = Math.min(totalPages - 1, currentPage + 1);

                  // Adjust if at the start
                  if (currentPage <= 2) {
                    endPage = 4;
                  }
                  // Adjust if at the end
                  if (currentPage >= totalPages - 1) {
                    startPage = totalPages - 3;
                  }

                  // Add ellipsis after first page if needed
                  if (startPage > 2) {
                    pages.push(<PageEllipsis key="start">...</PageEllipsis>);
                  }

                  // Add middle pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <PageButton
                        key={i}
                        active={currentPage === i}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </PageButton>
                    );
                  }

                  // Add ellipsis before last page if needed
                  if (endPage < totalPages - 1) {
                    pages.push(<PageEllipsis key="end">...</PageEllipsis>);
                  }

                  // Always show last page
                  pages.push(
                    <PageButton
                      key={totalPages}
                      active={currentPage === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </PageButton>
                  );
                }
                return pages;
              })()}
            </PageNumbers>
            <PaginationButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationControls>
        </PaginationContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditFromMenu}>Edit</MenuItem>
        {selectedRequest?.status === "new" && (
          <MenuItem onClick={handleCategoryMenuClick}>
            Request Type
            <ArrowRight style={{ marginLeft: "auto" }} />
          </MenuItem>
        )}
        <Menu
          anchorEl={categoryMenuAnchorEl}
          open={Boolean(categoryMenuAnchorEl)}
          onClose={handleCategoryMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          {callTypes.map((type) => (
            <MenuItem
              key={type.id}
              onClick={() => handleCategorySelect(type.type)}
            >
              {type.type}
            </MenuItem>
          ))}
        </Menu>
      </Menu>

      <Menu
        anchorEl={shareMenuAnchorEl}
        open={Boolean(shareMenuAnchorEl)}
        onClose={handleShareMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {departments
          .filter((dept) => dept.active)
          .map((dept) => (
            <MenuItem
              key={dept.id}
              onClick={() => handleDepartmentToggle(dept.id)}
            >
              <Checkbox
                checked={!!selectedDepartments[dept.id]}
                size="small"
                sx={{
                  color: "#FF7234",
                  "&.Mui-checked": {
                    color: "#FF7234",
                  },
                }}
              />
              {dept.name}
            </MenuItem>
          ))}
        <Divider />
        <MenuItem onClick={handleForwardSubmit}>
          <Button variant="primary" fullWidth>
            FORWARD SELECTED
          </Button>
        </MenuItem>
      </Menu>

      <PrayerRequestDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title="New Prayer Request Submission"
        request={newRequest}
        onChange={setNewRequest}
        onSubmit={handleSubmit}
        isNew={true}
        sources={SOURCES}
      />

      <PrayerRequestDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Edit Prayer Request Submission"
        request={editingRequest}
        onChange={setEditingRequest}
        onSubmit={handleEditSubmit}
        sources={SOURCES}
      />

      <PrayerTypeDialog
        open={prayerTypeDialogOpen}
        onClose={() => setPrayerTypeDialogOpen(false)}
        onConfirm={handlePrayerTypeConfirm}
        request={prayerRequestForType}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background: #faf8f8;
  min-height: 100vh;
  font-family: "Space Grotesk", sans-serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 24px;
    margin-bottom: 1.5rem;
    font-family: "Space Grotesk", sans-serif;
    font-weight: 600;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
`;

const NewRequestButton = styled.button`
  background: #2db3c0;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  font-family: "Space Grotesk", sans-serif;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    background: #259aa5;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  border-bottom: 1px solid #e5e7eb;
  justify-content: center;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
`;

const Tab = styled.button`
  padding: 0.75rem 1rem;
  border: ${(props) => {
    if (props.active) {
      switch (props.tab) {
        case "submitted":
          return "2px solid #2DB3C0";
        case "pending":
          return "2px solid #00323B";
        case "processed":
          return "2px solid #FF7234";
        case "deleted":
          return "2px solid #EC3310";
        default:
          return "2px solid #2DB3C0";
      }
    }
    return "none";
  }};
  background: white;
  color: ${(props) => {
    switch (props.tab) {
      case "submitted":
        return "#2DB3C0";
      case "pending":
        return "#00323B";
      case "processed":
        return "#FF7234";
      case "deleted":
        return "#EC3310";
      default:
        return "#2DB3C0";
    }
  }};
  font-weight: 700;
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  font-family: "Space Grotesk", sans-serif;
  letter-spacing: 0.25em;
  font-size: 14px;

  &:hover {
    background: ${(props) => (props.active ? "transparent" : "#F3F4F6")};
  }
`;

const TabCount = styled.span`
  background: ${(props) => {
    if (props) {
      switch (props.tab) {
        case "submitted":
          return "#2DB3C0";
        case "pending":
          return "#00323B";
        case "processed":
          return "#FF7234";
        case "deleted":
          return "#EC3310";
        default:
          return "#2DB3C0";
      }
    }
    return "#E5E7EB";
  }};
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  margin-left: 0.5rem;
  font-size: 12px;
  font-family: "Space Grotesk", sans-serif;
  letter-spacing: 0.1em;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: #c1dcde;
  padding: 1rem;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserName = styled.span`
  font-weight: 600;
  color: #00323b;
  font-size: 0.875rem;
  font-family: "Space Grotesk", sans-serif;
`;

const UserEmail = styled.span`
  color: #ec3310;
  font-size: 0.75rem;
  text-decoration: none;
  font-family: "Space Grotesk", sans-serif;
`;

const MoreButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #666666;
  cursor: pointer;

  &:hover {
    color: #2db3c0;
  }
`;

const CardContent = styled.p`
  color: #000000;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
  background: white;
  padding: 1rem;
  border-radius: 8px;
  font-family: "Space Grotesk", sans-serif;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  border-top: 1px solid #e5e7eb;
  padding: 1rem;
  background: white;
`;

const IconButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #666666;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    color: #2db3c0;
  }

  svg {
    font-size: 1.25rem;
  }
`;

const RequestMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #6b7280;
  font-size: 0.75rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PaginationInfo = styled.span`
  color: #6b7280;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const PaginationButton = styled.button`
  border: none;
  background: none;
  color: ${(props) => (props.disabled ? "#D1D5DB" : "#2DB3C0")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &:hover {
    text-decoration: ${(props) => (props.disabled ? "none" : "underline")};
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem;
  min-width: 2rem;
  border: 1px solid #e5e7eb;
  background: ${(props) => (props.active ? "#2DB3C0" : "white")};
  color: ${(props) => (props.active ? "white" : "#374151")};
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.active ? "#2DB3C0" : "#F3F9FA")};
    border-color: ${(props) => (props.active ? "#2DB3C0" : "#D1D5DB")};
  }
`;

const CategoryChip = styled.span`
  background: #e5e7eb;
  color: #374151;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
`;

const DialogButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  background: ${(props) => (props.primary ? "#2DB3C0" : "transparent")};
  color: ${(props) => (props.primary ? "white" : "#6B7280")};

  &:hover {
    background: ${(props) => (props.primary ? "#259AA5" : "#F3F9FA")};
  }
`;

const PageEllipsis = styled.span`
  color: #6b7280;
  padding: 0.5rem;
`;

const SourceLabel = styled.span`
  text-transform: uppercase;
  color: #666666;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: "Space Grotesk", sans-serif;
`;

const StyledDialogContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledDialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #666666;
  padding: 4px;

  &:hover {
    color: #000000;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #2db3c0;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2db3c0;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
`;

const RequestId = styled.span`
  color: #666666;
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.variant === "primary" &&
    `
    background: #2DB3C0;
    color: white;
    border: none;

    &:hover {
      background: #259AA5;
    }
  `}

  ${(props) =>
    props.variant === "secondary" &&
    `
    background: #F97316;
    color: white;
    border: none;

    &:hover {
      background: #EA580C;
    }
  `}

  ${(props) =>
    props.variant === "text" &&
    `
    background: none;
    color: #666666;
    border: none;

    &:hover {
      background: #F3F4F6;
    }
  `}
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #2db3c0;
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 16px;
`;

const CategoryButton = styled.button`
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    border-color: #2db3c0;
  }
`;

const Dialog = styled.div`
  padding: 24px;
  background: white;
  border-radius: 12px;
  max-width: ${(props) => props.maxWidth || "400px"};
  width: 100%;
  max-height: ${(props) => props.maxHeight || "auto"};
  overflow-y: auto;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #2db3c0;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const CategoryTitle = styled.h3`
  margin: 16px 0;
  color: #374151;
  font-size: 1.125rem;
  font-weight: 600;
`;

const DeleteButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;

const PrayedButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};

  &:hover {
    opacity: ${(props) => (props.disabled ? "0.5" : "0.8")};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ShareButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};

  &:hover {
    opacity: ${(props) => (props.disabled ? "0.5" : "0.8")};
  }
`;

const RecoverButton = styled.button`
  border: none;
  background: #ec3310;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    opacity: 0.9;
  }
`;

const PrayerCountInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-top: 1px solid #e5e7eb;
`;

const PrayerCount = styled.span`
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: "Space Grotesk", sans-serif;
`;

const PrayerCompleteBadge = styled.span`
  background: #2db3c0;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: "Space Grotesk", sans-serif;
`;

export default PrayerRequestDashboard;
